import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/CustomTable/CustomTable";
import SatisfactionSurveysColumns from "../../../components/CustomTable/Columns/SatisfactionSurveysColumns";
import useSatisfactionSurveys from "../../../hooks/useSatisfactionSurveys";
import useAxios from "../../../hooks/useAxios";
import { AiFillStar } from "react-icons/ai";
import { FiSmile, FiCheckCircle, FiRotateCcw } from "react-icons/fi";

const SCORE_LABELS = {
    5: { label: "5 Estrellas", color: "#10b981", bg: "#dcfce7" },
    4: { label: "4 Estrellas", color: "#3b82f6", bg: "#dbeafe" },
    3: { label: "3 Estrellas", color: "#eab308", bg: "#fef9c3" },
    2: { label: "2 Estrellas", color: "#f97316", bg: "#ffedd5" },
    1: { label: "1 Estrella", color: "#ef4444", bg: "#fee2e2" },
};

const SatisfactionSurveys = () => {
    const entity = {
        name: "Encuestas de satisfacción",
        url: "satisfaction-surveys",
        frontendUrl: "/encuestas-de-satisfaccion",
        camelName: "satisfactionSurveys",
    };

    const [filters, setFilters] = useState({
        page: 1,
        search: "",
        score: "",
        start: "",
        end: "",
        perPage: 10,
    });

    const [{ satisfactionSurveys, total, numberOfPages, loading }, getSurveys] =
        useSatisfactionSurveys({
            params: { ...filters },
            options: { useCache: false },
        });

    const [{ data: summaryData }, getSummary] = useAxios(
        { url: "/satisfaction-surveys/summary" },
        { useCache: false }
    );

    useEffect(() => {
        getSurveys({
            params: filters,
        });
    }, [filters]);

    const handleFilterChange = (e) => {
        setFilters((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
            page: 1,
        }));
    };

    const handleSelectScoreFilter = (scoreValue) => {
        setFilters((prev) => ({
            ...prev,
            score: prev.score === scoreValue ? "" : scoreValue,
            page: 1,
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            page: 1,
            search: "",
            score: "",
            start: "",
            end: "",
            perPage: 10,
        });
    };

    const handlePageChange = (page) => {
        if (numberOfPages >= page && page > 0) {
            setFilters((prev) => ({
                ...prev,
                page,
            }));
        }
    };

    const totalSurveys = summaryData?.totalSurveys || total || 0;
    const averageScore = summaryData?.averageScore || 0;
    const satisfactionRate = summaryData?.satisfactionRate || 0;
    const distribution = summaryData?.distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    return (
        <div className="container-fluid py-4">
            {/* Header del Módulo */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                <div>
                    <h3 className="fw-bold mb-1" style={{ color: "#0f172a" }}>
                        Encuestas de Satisfacción
                    </h3>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Retroalimentación y valoraciones registradas al momento del cierre de planificaciones
                    </p>
                </div>
            </div>

            {/* Tarjetas KPI de Resumen */}
            <div className="row g-3 mb-4">
                {/* KPI 1: Calificación Promedio */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", borderLeft: "4px solid #f59e0b" }}>
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                                <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                    Promedio General
                                </span>
                                <div className="d-flex align-items-baseline gap-2 mt-1">
                                    <h2 className="fw-bold mb-0" style={{ color: "#0f172a" }}>
                                        {averageScore}
                                    </h2>
                                    <span className="text-muted" style={{ fontSize: "0.9rem" }}>/ 5.0</span>
                                </div>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <AiFillStar
                                            key={star}
                                            size={16}
                                            color={star <= Math.round(averageScore) ? "#f59e0b" : "#e2e8f0"}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "10px",
                                    backgroundColor: "#fef3c7",
                                    color: "#d97706",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AiFillStar size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPI 2: Total Encuestas */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", borderLeft: "4px solid #3b82f6" }}>
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                                <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                    Total Respuestas
                                </span>
                                <h2 className="fw-bold my-1" style={{ color: "#0f172a" }}>
                                    {totalSurveys}
                                </h2>
                                <small className="text-muted" style={{ fontSize: "0.78rem" }}>
                                    Planes cerrados con evaluación
                                </small>
                            </div>
                            <div
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "10px",
                                    backgroundColor: "#eff6ff",
                                    color: "#2563eb",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FiSmile size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPI 3: Tasa de Aprobación */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", borderLeft: "4px solid #10b981" }}>
                        <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                                <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                    Satisfacción Positiva
                                </span>
                                <h2 className="fw-bold my-1" style={{ color: "#0f172a" }}>
                                    {satisfactionRate}%
                                </h2>
                                <small className="text-muted" style={{ fontSize: "0.78rem" }}>
                                    Calificaciones de 4 y 5 estrellas
                                </small>
                            </div>
                            <div
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "10px",
                                    backgroundColor: "#ecfdf5",
                                    color: "#059669",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FiCheckCircle size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPI 4: Desglose por Estrellas */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <span className="text-muted text-uppercase fw-semibold d-block mb-2" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>
                                Desglose de Puntuación
                            </span>
                            <div className="d-flex flex-column gap-1">
                                {[5, 4, 3, 2, 1].map((s) => {
                                    const count = distribution[s] || 0;
                                    const pct = totalSurveys > 0 ? Math.round((count / totalSurveys) * 100) : 0;
                                    const isSelected = String(filters.score) === String(s);

                                    return (
                                        <div
                                            key={s}
                                            onClick={() => handleSelectScoreFilter(String(s))}
                                            style={{
                                                cursor: "pointer",
                                                background: isSelected ? "#f1f5f9" : "transparent",
                                                padding: "1px 4px",
                                                borderRadius: "4px",
                                                fontSize: "0.75rem",
                                            }}
                                            className="d-flex align-items-center gap-2"
                                            title={`Filtrar por ${s} estrellas`}
                                        >
                                            <span style={{ width: 14, fontWeight: 600 }}>{s}★</span>
                                            <div className="progress flex-grow-1" style={{ height: 6 }}>
                                                <div
                                                    className="progress-bar"
                                                    style={{
                                                        width: `${pct}%`,
                                                        backgroundColor: SCORE_LABELS[s].color,
                                                    }}
                                                />
                                            </div>
                                            <span className="text-muted" style={{ width: 22, textAlign: "right" }}>
                                                {count}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra de Filtros */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                <div className="card-body p-3">
                    <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-4">
                            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "0.75rem" }}>
                                Búsqueda general
                            </label>
                            <input
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                                placeholder="Buscar por usuario, comentario, centro de costo..."
                                className="form-control form-control-sm"
                                style={{ borderRadius: "6px" }}
                            />
                        </div>

                        <div className="col-6 col-md-2">
                            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "0.75rem" }}>
                                Puntuación
                            </label>
                            <select
                                name="score"
                                value={filters.score}
                                onChange={handleFilterChange}
                                className="form-select form-select-sm"
                                style={{ borderRadius: "6px" }}
                            >
                                <option value="">Todas</option>
                                <option value="5">5 ★ (Muy satisfecho)</option>
                                <option value="4">4 ★ (Satisfecho)</option>
                                <option value="3">3 ★ (Regular)</option>
                                <option value="2">2 ★ (Insatisfecho)</option>
                                <option value="1">1 ★ (Muy insatisfecho)</option>
                            </select>
                        </div>

                        <div className="col-6 col-md-2">
                            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "0.75rem" }}>
                                Fecha Desde
                            </label>
                            <input
                                type="date"
                                name="start"
                                value={filters.start}
                                onChange={handleFilterChange}
                                className="form-control form-control-sm"
                                style={{ borderRadius: "6px" }}
                            />
                        </div>

                        <div className="col-6 col-md-2">
                            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "0.75rem" }}>
                                Fecha Hasta
                            </label>
                            <input
                                type="date"
                                name="end"
                                value={filters.end}
                                onChange={handleFilterChange}
                                className="form-control form-control-sm"
                                style={{ borderRadius: "6px" }}
                            />
                        </div>

                        <div className="col-6 col-md-2 d-flex gap-2">
                            {(filters.search || filters.score || filters.start || filters.end) && (
                                <button
                                    onClick={handleResetFilters}
                                    className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
                                    style={{ borderRadius: "6px" }}
                                    title="Limpiar filtros"
                                >
                                    <FiRotateCcw size={13} />
                                    <span>Limpiar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabla de Resultados */}
            <CustomTable
                loading={loading}
                title={entity.name}
                entity={entity.url}
                pages={numberOfPages}
                total={total}
                values={satisfactionSurveys}
                currentPage={filters.page}
                collumns={SatisfactionSurveysColumns}
                changePage={handlePageChange}
                filters={filters}
                excelUrl={`${entity.url}/export/excel`}
                perPage={filters.perPage}
                hideSelectAll={true}
                withoutGlobalActions={true}
                onPerPageChange={(e) =>
                    setFilters((prev) => ({
                        ...prev,
                        [e.target.name]: e.target.value,
                        page: 1,
                    }))
                }
            />
        </div>
    );
};

export default SatisfactionSurveys;
